// src/encounter/structured-note/chip-choice.tsx
//
// The note's choice control as VISIBLE CHIPS to click (19 Oct, Antonin: "ROS etc. should show clickable chips like HPI"; the chief
// complaint's table, `complaint-lines.tsx`): the item's own options in the template's order, a chosen chip pressed, a long group
// showing its first `CHIPS_SHOWN` chips (and every chosen one) with "+N more". It stands where the DS closed `PickListChip` stood
// (Risk choices, Yes / No, the ROS and exam system picks and findings), with the same value and change contract, so every answer
// still goes through the caller's own write (setAnswer / the Risk bridge). Look: `[data-cc-chip]` in `src/styles/showcase-bridge.css`.
import { useState } from 'react';
import { Button } from '@scripsteam/scrips-signal-ds';
import { Check } from 'lucide-react';

/** A long group shows this many chips (and every chosen one); the rest sit behind "+N more". No usage data reaches the app, so
 *  the template's own order decides which come first. */
export const CHIPS_SHOWN = 8;

export interface ChipOption {
  value: string;
  label: string;
  /** What the option records (shown as the chip's tooltip). */
  description?: string;
}

type ChipChoiceBase = {
  options: readonly ChipOption[];
  'aria-label': string;
  readOnly?: boolean;
  /** The item is required: the group says so to assistive tech; the sign gate (lib/required-items.ts) is unchanged. */
  requiredLabel?: string;
  /** Single choice only: clicking the chosen chip again calls `onChange('')` (the caller clears the item). */
  clearable?: boolean;
};
export type ChipChoiceProps = ChipChoiceBase &
  (
    | { multiple: true; value: readonly string[]; onChange: (next: string[]) => void }
    | { multiple?: false; value: string | null; onChange: (next: string) => void }
  );

export function ChipChoice(props: ChipChoiceProps) {
  const { options, readOnly, requiredLabel } = props;
  const [expanded, setExpanded] = useState(false);
  const chosen = props.multiple ? props.value : props.value == null ? [] : [props.value];
  if (readOnly) {
    const text = options.filter((o) => chosen.includes(o.value)).map((o) => o.label).join(', ');
    return (
      <span className={text ? 'text-text-primary' : 'text-text-secondary'} data-cc-value>
        {text || 'Not asked'}
      </span>
    );
  }
  const pick = (value: string) => {
    if (props.multiple) props.onChange(chosen.includes(value) ? chosen.filter((v) => v !== value) : [...chosen, value]);
    else if (!chosen.includes(value)) props.onChange(value);
    else if (props.clearable) props.onChange('');
  };
  const shown = expanded ? options : options.filter((o, i) => i < CHIPS_SHOWN || chosen.includes(o.value));
  const hidden = options.length - shown.length;
  return (
    <div role="group" aria-label={props['aria-label']} aria-required={requiredLabel ? true : undefined} data-cc-chips className="flex flex-wrap items-center gap-2">
      {shown.map((o) => {
        const on = chosen.includes(o.value);
        return (
          <button key={o.value} type="button" data-cc-chip="" aria-pressed={on} title={o.description} onClick={() => pick(o.value)}>
            {on && <Check className="size-4" aria-hidden />}
            {o.label}
          </button>
        );
      })}
      {hidden > 0 && (
        <Button variant="tertiary" size="sm" aria-expanded={false} onClick={() => setExpanded(true)}>
          +{hidden} more
        </Button>
      )}
      {expanded && options.length > CHIPS_SHOWN && (
        <Button variant="tertiary" size="sm" aria-expanded onClick={() => setExpanded(false)}>
          Show fewer
        </Button>
      )}
    </div>
  );
}
